import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  FileText,
  Check,
  X,
  Clock,
  RefreshCcw,
  CheckCircle2,
  Circle,
  AlertTriangle,
  Download,
  Eye,
  Archive,
  PartyPopper,
  PackageCheck,
  Pencil,
  Cloud,
  CloudDownload,
  AlertCircle,
  Loader2,
  ChevronRight,
} from "lucide-react";
import AppLayout, { PageHeader } from "../components/AppLayout";
import StatusBadge, { ChellyStatus } from "../components/StatusBadge";
import PdfViewerModal from "../components/PdfViewerModal";
import RelancePartner from "../components/RelancePartner";
import DossierSynthese from "../components/DossierSynthese";
import DossierHeaderSticky from "../components/DossierHeaderSticky";
import DossierPhaseTabs from "../components/DossierPhaseTabs";
import DossierHistoryDrawer from "../components/DossierHistoryDrawer";
import { api, API } from "../lib/api";
import { useLive } from "../lib/live";
import { celebrate } from "../lib/confetti";
import { toast } from "sonner";

export default function DossierDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [dossier, setDossier] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [previewIndex, setPreviewIndex] = useState(null);
  const [reclassifyTarget, setReclassifyTarget] = useState(null);
  const [pullingDrive, setPullingDrive] = useState(false);
  const [piecesCollapsed, setPiecesCollapsed] = useState(true);  // collapsed by default to shorten the page

  const load = async () => {
    try {
      const { data } = await api.get(`/dossiers/${id}`);
      setDossier(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [id]);

  // Live updates: subscribe to step / completed events for THIS dossier
  const { subscribe } = useLive();
  useEffect(() => {
    return subscribe((event) => {
      if (!event.payload || event.payload.id !== id) return;
      if (event.type === "dossier:step") {
        // Append the new step to the workflow_log without full refetch
        setDossier((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            status: event.payload.status || prev.status,
            workflow_log: [...(prev.workflow_log || []), event.payload.step],
          };
        });
      } else if (event.type === "dossier:completed") {
        // Full reload to get extraction + pieces + attachments
        load();
      }
    });
  }, [id, subscribe]);

  const isProcessing = dossier?.status === "PROCESSING";

  const toggleAttachmentValidation = async (att) => {
    const wasValidated = !!att.validated;
    const atts = dossier?.attachments || [];
    const idx = atts.indexOf(att);
    // Optimistic update
    setDossier((prev) => ({
      ...prev,
      attachments: (prev.attachments || []).map((a, i) =>
        i === idx ? { ...a, validated: !wasValidated } : a
      ),
    }));
    try {
      const { data } = await api.post(
        `/dossiers/${id}/attachment-by-index/${idx}/validate`
      );
      if (data.all_validated && !wasValidated) {
        celebrate();
        toast.success("🎉 Toutes les pièces sont validées !", {
          description: "Tu peux maintenant marquer le dossier COMPLET.",
          duration: 5000,
        });
      } else if (!wasValidated) {
        toast.success(`✓ Pièce validée (${data.validated_count}/${data.total})`, { duration: 1500 });
      }
    } catch (e) {
      // Revert on failure
      const atts = dossier?.attachments || [];
      const idx = atts.indexOf(att);
      setDossier((prev) => ({
        ...prev,
        attachments: (prev.attachments || []).map((a, i) =>
          i === idx ? { ...a, validated: wasValidated } : a
        ),
      }));
      toast.error("Impossible de mettre à jour la validation");
    }
  };

  const retry = async () => {
    if (!window.confirm("Retraiter ce dossier ? L'historique sera remplacé.")) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/dossiers/${id}/retry`);
      navigate(`/dossiers/${data.dossier_id}`);
    } finally {
      setBusy(false);
    }
  };

  const markManual = async () => {
    if (!window.confirm("Marquer ce dossier comme traité manuellement ?")) return;
    setBusy(true);
    try {
      await api.post(`/dossiers/${id}/mark-manual`);
      await load();
    } finally {
      setBusy(false);
    }
  };

  const archiveDossier = async () => {
    if (!window.confirm("Archiver ce dossier ? Il passera en colonne 'Archivés'.")) return;
    setBusy(true);
    try {
      await api.post(`/dossiers/${id}/status`, { status: "TRAITE" });
      toast.success("📦 Dossier archivé");
      await load();
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="h-32 bg-white border border-norea-border rounded-lg animate-pulse" />
      </AppLayout>
    );
  }

  if (!dossier) {
    return (
      <AppLayout>
        <PageHeader title="Dossier introuvable" />
        <button onClick={() => navigate(-1)} className="btn-secondary">
          <ArrowLeft className="w-4 h-4" /> Retour
        </button>
      </AppLayout>
    );
  }

  const ex = dossier.extraction || {};
  const token = localStorage.getItem("norea_token");

  const piecesBlock = (
 <>
          {dossier.pieces_attendues?.length > 0 && (
            <div className="card-flat p-5" data-testid="pieces-card">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="overline">Complétude du dossier</div>
                  <h3 className="font-heading text-lg font-medium text-norea-text mt-1">
                    {dossier.pieces_presentes?.length || 0} / {dossier.pieces_attendues.length} pièces
                  </h3>
                </div>
                <StatusBadge status={dossier.status} />
                <ChellyStatus d={dossier} />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {dossier.pieces_attendues.map((p) => {
                  const present = dossier.pieces_presentes?.includes(p);
                  // Find detection details (which PDF + page range) for this piece
                  const detection = (dossier.all_detected_pieces || []).find(
                    (d) => d.type_piece === p || d.types_couverts?.includes(p)
                  );
                  const pageHint = detection?.page_debut
                    ? detection.page_fin && detection.page_fin !== detection.page_debut
                      ? `p.${detection.page_debut}-${detection.page_fin}`
                      : `p.${detection.page_debut}`
                    : null;
                  return (
                    <div
                      key={p}
                      className={`flex items-center gap-2 px-3 py-2 rounded-md border text-sm ${
                        present
                          ? "bg-status-completBg border-status-completBorder text-status-completText"
                          : "bg-status-incompletBg border-status-incompletBorder text-status-incompletText"
                      }`}
                      data-testid={`piece-${present ? "present" : "missing"}`}
                      title={detection?.personne ? `Personne : ${detection.personne}` : undefined}
                    >
                      {present ? <Check className="w-4 h-4 flex-shrink-0" /> : <X className="w-4 h-4 flex-shrink-0" />}
                      <span className="truncate flex-1">{p}</span>
                      {pageHint && (
                        <span className="text-[10px] font-mono opacity-70 shrink-0">{pageHint}</span>
                      )}
                    </div>
                  );
                })}
              </div>
              {dossier.all_detected_pieces?.length > 0 && (
                <div className="mt-3 text-[11px] text-norea-textMuted">
                  ℹ️ {dossier.all_detected_pieces.length} pièce(s) détectée(s) au total dans le PDF (multi-pages analysées par l'IA)
                </div>
              )}
            </div>
          )}
  </>
  );
  const attachmentsBlock = (
          <div className="card-flat p-5" data-testid="attachments-card">
            {(() => {
              const atts = (dossier.attachments || []).filter((a) => !a.is_original);
              const validatedCount = atts.filter((a) => a.validated).length;
              const totalCount = atts.length;
              const allValidated = totalCount > 0 && validatedCount === totalCount;
              return (
                <>
                  <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
                    <button
                      onClick={() => setPiecesCollapsed(!piecesCollapsed)}
                      className="flex-1 flex items-start gap-2 text-left -m-2 p-2 rounded hover:bg-norea-bg transition group"
                      data-testid="btn-toggle-pieces"
                      aria-expanded={!piecesCollapsed}
                    >
                      <ChevronRight
                        className={`w-4 h-4 text-norea-textMuted group-hover:text-norea-primary mt-0.5 transition-transform flex-shrink-0 ${
                          piecesCollapsed ? "" : "rotate-90"
                        }`}
                      />
                      <div className="flex-1">
                        <div className="overline">Pièces individualisées ({totalCount})</div>
                        {!piecesCollapsed && (
                          <div className="text-[11px] text-norea-textMuted mt-1">
                            Pièces extraites du PDF original par l'IA · cliquez pour visualiser · cochez pour valider
                          </div>
                        )}
                      </div>
                    </button>
                    {totalCount > 0 && (
                      <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                        <div
                          className={`flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-md border font-medium ${
                            allValidated
                              ? "bg-status-completBg border-status-completBorder text-status-completText"
                              : "bg-norea-surfaceHover border-norea-border text-norea-textMuted"
                          }`}
                          data-testid="validation-progress"
                        >
                          {allValidated ? <PartyPopper className="w-3.5 h-3.5" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                          <span className="tabular-nums">{validatedCount}/{totalCount}</span>
                          <span>validées</span>
                        </div>
                        {!piecesCollapsed && (
                          <button
                            onClick={async () => {
                              const r = await api.get(`/dossiers/${dossier.id}/zip?normalized=1`, { responseType: "blob" });
                              const url = URL.createObjectURL(r.data);
                              const link = document.createElement("a");
                              link.href = url;
                              const cd = r.headers["content-disposition"] || "";
                              const m = cd.match(/filename="?([^"]+)"?/);
                              link.download = m ? m[1] : `dossier-${dossier.id.slice(0, 8)}.zip`;
                              link.click();
                              URL.revokeObjectURL(url);
                            }}
                            className="btn-secondary text-xs"
                            data-testid="btn-download-zip"
                          >
                            <Archive className="w-3.5 h-3.5" />
                            Tout télécharger (ZIP)
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                  {!piecesCollapsed && (
                  <div className="divide-y divide-norea-border" data-testid="pieces-list">
                    {atts.map((a, i) => {
                      const globalIdx = (dossier.attachments || []).indexOf(a);
                      return (
                      <div
                        key={i}
                        className={`flex items-center gap-3 py-3 px-2 -mx-2 rounded-md cursor-pointer transition-colors group ${
                          a.validated ? "bg-status-completBg/40 hover:bg-status-completBg" : "hover:bg-norea-surfaceHover"
                        }`}
                        onClick={() => setPreviewIndex(globalIdx)}
                        data-testid={`attachment-${i}`}
                        title="Cliquer pour visualiser le PDF"
                      >
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleAttachmentValidation(a);
                          }}
                          className={`w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0 transition ${
                            a.validated
                              ? "bg-status-completText text-white"
                              : "bg-white border-2 border-norea-border hover:border-norea-primary"
                          }`}
                          title={a.validated ? "Cliquer pour invalider" : "Cliquer pour valider"}
                          data-testid={`btn-validate-${i}`}
                        >
                          {a.validated ? <Check className="w-3.5 h-3.5" /> : null}
                        </button>
                        <div
                          className={`w-10 h-10 rounded-md flex items-center justify-center flex-shrink-0 ${
                            a.is_bdc ? "bg-norea-primary text-white" : "bg-norea-surfaceHover text-norea-textMuted group-hover:bg-white"
                          }`}
                        >
                          <FileText className="w-4 h-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className={`text-sm font-medium truncate ${a.validated ? "line-through text-norea-textMuted" : "text-norea-text group-hover:text-norea-primary"}`} data-testid={`attachment-renamed-${i}`}>
                            {a.normalized_filename || a.filename}
                          </div>
                          <div className="text-xs text-norea-textMuted flex items-center gap-2 flex-wrap">
                            {a.is_bdc && (
                              <span className="inline-flex items-center px-1.5 py-0.5 text-[9px] uppercase tracking-wider font-semibold bg-norea-primary text-white rounded">
                                BDC
                              </span>
                            )}
                            {a.type_piece && (
                              <span className="inline-flex items-center px-1.5 py-0.5 text-[9px] uppercase tracking-wider font-semibold bg-norea-surfaceHover text-norea-text border border-norea-border rounded">
                                {a.type_piece}
                              </span>
                            )}
                            {a.type_piece === "Autre" && a.titre_doc && (
                              <span
                                className="inline-flex items-center px-1.5 py-0.5 text-[10px] italic font-medium text-norea-accent"
                                title="Titre du document détecté par l'IA"
                              >
                                « {a.titre_doc} »
                              </span>
                            )}
                            <span className="truncate" title={a.filename}>nom d'origine : {a.filename}</span>
                            {a.size ? <span>· {(a.size / 1024).toFixed(0)} Ko</span> : null}
                          </div>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                          <button
                            onClick={() => setPreviewIndex(globalIdx)}
                            className="btn-secondary text-xs"
                            data-testid={`btn-preview-${i}`}
                            title="Visualiser dans la fenêtre"
                          >
                            <Eye className="w-3.5 h-3.5" />
                            Visualiser
                          </button>
                          <button
                            onClick={() => setReclassifyTarget({ idx: (dossier.attachments || []).indexOf(a), att: a })}
                            className="p-1.5 text-norea-textMuted hover:text-norea-primary transition"
                            title="Reclasser la pièce"
                            data-testid={`btn-reclassify-${i}`}
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={async () => {
                              const globalIdx = (dossier.attachments || []).indexOf(a);
                              const r = await api.get(
                                `/dossiers/${dossier.id}/attachment-by-index/${globalIdx}?normalized=1`,
                                { responseType: "blob" }
                              );
                              const url = URL.createObjectURL(r.data);
                              const link = document.createElement("a");
                              link.href = url;
                              link.download = a.normalized_filename || a.filename;
                              link.click();
                              URL.revokeObjectURL(url);
                            }}
                            className="btn-ghost text-xs"
                            data-testid={`btn-download-${i}`}
                            title={`Télécharger sous le nom : ${a.normalized_filename || a.filename}`}
                          >
                            <Download className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                      );
                    })}
                  </div>
                  )}
                </>
              );
            })()}
          </div>
  );
  const verifPopup = <div className="space-y-4">{piecesBlock}{attachmentsBlock}</div>;

  return (
    <AppLayout>
      <div className="flex items-center justify-between mb-4" data-testid="dossier-detail-topbar">
        <nav className="flex items-center gap-1.5 text-xs text-norea-textMuted">
          <button onClick={() => navigate("/dossiers")} className="hover:text-norea-primary" data-testid="link-back-dossiers">Dossiers</button>
          <ChevronRight className="w-3 h-3" />
          <span className="text-norea-text font-medium">{ex.nom || "?"} {ex.prenom || ""}</span>
        </nav>
        <div className="flex items-center gap-2">
          <button onClick={() => navigate(-1)} className="btn-ghost" data-testid="btn-back">
            <ArrowLeft className="w-4 h-4" /> Retour
          </button>
          <button onClick={retry} disabled={busy} className="btn-secondary" data-testid="btn-retry">
            <RefreshCcw className="w-4 h-4" /> Retraiter
          </button>
          {dossier.status === "COMPLET" && (
            <button onClick={archiveDossier} disabled={busy} className="btn-primary" data-testid="btn-archive">
              <PackageCheck className="w-4 h-4" /> Archiver
            </button>
          )}
          {dossier.status !== "COMPLET" && dossier.status !== "TRAITE" && (
            <button onClick={markManual} disabled={busy} className="btn-primary" data-testid="btn-mark-manual">
              <CheckCircle2 className="w-4 h-4" /> Marquer traité
            </button>
          )}
        </div>
      </div>

      {/* Sticky header: client summary + horizontal workflow */}
      <DossierHeaderSticky dossier={dossier} onDossierUpdated={load} />

      {/* Right-side collapsible history drawer */}
      <DossierHistoryDrawer dossierId={id} />

      {/* 6-tab shell: "Vérif documents" renders the existing content; the 5 others are Phase 2/3 placeholders */}
      <DossierPhaseTabs
        dossier={dossier}
        onDossierUpdated={load}
        syntheseContent={
          <DossierSynthese dossier={dossier} onDossierUpdated={load} verifPopup={verifPopup} />
        }
        verifDocumentsContent={
          <>

      {/* Banner: try to pull Google Drive links from the email body */}
      {(dossier.status === "A_VERIFIER" || (dossier.mail_body_html || dossier.mail_body_text || "").toLowerCase().match(/drive\.google\.com|docs\.google\.com/)) && (
        <div className="mt-4 p-4 rounded-lg border border-norea-accent/40 bg-norea-accent/10 flex items-start gap-3" data-testid="drive-pull-banner">
          <Cloud className="w-5 h-5 text-norea-accent flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="font-medium text-sm text-norea-text">Le mail contient peut-être des pièces jointes via Google Drive</div>
            <div className="text-xs text-norea-textMuted mt-0.5">
              Quand un PDF dépasse 25 Mo, Gmail le remplace par un lien Drive. Norea peut tenter de les télécharger automatiquement si le partage est public.
            </div>
            {(dossier.drive_errors || []).length > 0 && (
              <div className="mt-2 text-xs text-status-erreurText">
                ⚠ Échec(s) précédent(s) : {dossier.drive_errors.length} lien(s) restreint(s). Vérifie que le partage est « Toute personne disposant du lien » dans Drive.
              </div>
            )}
          </div>
          <button
            onClick={async () => {
              setPullingDrive(true);
              try {
                const { data } = await api.post(`/dossiers/${id}/pull-drive-links`);
                if (data.ok) {
                  toast.success(`${data.downloaded} fichier(s) Drive téléchargé(s) · re-traitement en cours…`);
                  if (data.new_dossier_id && data.new_dossier_id !== id) {
                    setTimeout(() => navigate(`/dossiers/${data.new_dossier_id}`), 1500);
                  } else {
                    setTimeout(load, 2500);
                  }
                } else {
                  toast.error(data.error || "Aucun lien Drive accessible");
                }
              } catch (e) {
                toast.error(e?.response?.data?.detail || "Erreur lors de la récupération Drive");
              } finally {
                setPullingDrive(false);
              }
            }}
            disabled={pullingDrive}
            className="btn-primary text-xs flex-shrink-0"
            data-testid="btn-pull-drive"
          >
            {pullingDrive ? <RefreshCcw className="w-3.5 h-3.5 animate-spin" /> : <CloudDownload className="w-3.5 h-3.5" />}
            Récupérer les pièces Drive
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
        {/* Left: workflow journal */}
        <div className="lg:col-span-1 space-y-4">
          <div className="card-flat p-5" data-testid="workflow-card">
            <div className="flex items-center justify-between mb-3">
              <div className="overline">Journal du workflow</div>
              {isProcessing && (
                <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded text-norea-primary bg-norea-primary/10 border border-norea-primary/30" data-testid="live-indicator">
                  <span className="w-1.5 h-1.5 rounded-full bg-norea-primary animate-pulse" />
                  En direct
                </span>
              )}
            </div>
            <div className="space-y-1.5">
              {(dossier.workflow_log || []).map((step, i) => {
                const cls =
                  step.status === "success"
                    ? "border-status-completBorder bg-status-completBg"
                    : step.status === "warning"
                    ? "border-status-incompletBorder bg-status-incompletBg"
                    : step.status === "error"
                    ? "border-status-erreurBorder bg-status-erreurBg"
                    : "border-norea-border bg-norea-bg";
                const Icon =
                  step.status === "success"
                    ? Check
                    : step.status === "warning"
                    ? AlertTriangle
                    : step.status === "error"
                    ? X
                    : Clock;
                return (
                  <div key={i} className={`flex items-start gap-2 px-2.5 py-1.5 border rounded-md ${cls}`} data-testid={`step-${i}`}>
                    <div className="w-5 h-5 rounded-full bg-white border border-current/20 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-3 h-3" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-xs font-medium truncate">{step.name}</span>
                        <span className="text-[10px] tabular-nums opacity-60 flex-shrink-0">{step.duration_ms || 0} ms</span>
                      </div>
                      {step.message && <div className="text-[11px] opacity-75 mt-0.5 leading-snug">{step.message}</div>}
                    </div>
                  </div>
                );
              })}
              {!(dossier.workflow_log?.length) && (
                <div className="text-xs text-norea-textMuted italic text-center py-2">Aucune étape</div>
              )}
            </div>
          </div>

        </div>

        {/* Right: pieces + workflow */}
        <div className="lg:col-span-2 space-y-4">
          {dossier.status === "INCOMPLET" && (
            <RelancePartner
              dossier={dossier}
              onUpdated={(patch) => setDossier((prev) => ({ ...prev, ...patch }))}
            />
          )}
          {piecesBlock}

          {/* Section: PDF originaux reçus par mail (pour vérifier le découpage) */}
          {(() => {
            const originals = (dossier.attachments || []).filter((a) => a.is_original);
            if (originals.length === 0) return null;
            return (
              <div className="card-flat p-5" data-testid="originals-card">
                <div className="overline mb-1">PDF originaux reçus par mail ({originals.length})</div>
                <div className="text-[11px] text-norea-textMuted mb-3">
                  Source brute pour vérifier que le découpage automatique a été correctement effectué.
                </div>
                <div className="divide-y divide-norea-border">
                  {originals.map((a) => {
                    const idx = (dossier.attachments || []).indexOf(a);
                    return (
                      <div
                        key={`orig-${idx}`}
                        className="flex items-center gap-3 py-3 px-2 -mx-2 rounded-md cursor-pointer hover:bg-norea-surfaceHover transition-colors group"
                        onClick={() => setPreviewIndex(idx)}
                        data-testid={`original-${idx}`}
                        title="Cliquer pour visualiser le PDF original"
                      >
                        <div className="w-10 h-10 rounded-md flex items-center justify-center flex-shrink-0 bg-norea-accent/15 text-norea-accent">
                          <FileText className="w-4 h-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium truncate text-norea-text group-hover:text-norea-primary">
                            {a.filename}
                          </div>
                          <div className="text-xs text-norea-textMuted flex items-center gap-2 flex-wrap">
                            <span className="inline-flex items-center px-1.5 py-0.5 text-[9px] uppercase tracking-wider font-semibold bg-norea-accent text-white rounded">
                              Original
                            </span>
                            {a.total_pages ? <span>{a.total_pages} page{a.total_pages > 1 ? "s" : ""}</span> : null}
                            {a.splits_count ? <span>· découpé en {a.splits_count} pièce{a.splits_count > 1 ? "s" : ""}</span> : null}
                            <span>· {Math.round((a.size || 0) / 1024)} Ko</span>
                          </div>
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            window.open(`${API}/dossiers/${dossier.id}/attachment/${encodeURIComponent(a.filename)}?inline=1`, "_blank");
                          }}
                          className="opacity-0 group-hover:opacity-100 transition btn-ghost text-xs"
                          data-testid={`btn-original-open-${idx}`}
                          title="Ouvrir dans un nouvel onglet"
                        >
                          Ouvrir
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          {attachmentsBlock}

          {dossier.trello_card_url && (
            <a
              href={dossier.trello_card_url}
              target="_blank"
              rel="noreferrer"
              className="btn-secondary inline-flex"
              data-testid="link-trello"
            >
              Voir la carte Trello
            </a>
          )}
        </div>
      </div>
          </>
        }
      />

      {previewIndex !== null && dossier.attachments?.[previewIndex] && (
        <PdfViewerModal
          dossierId={dossier.id}
          attachment={dossier.attachments[previewIndex]}
          index={previewIndex}
          onClose={() => setPreviewIndex(null)}
          onValidationToggle={(patch) => {
            // Patch only the toggled item; never touch the rest.
            setDossier((prev) => ({
              ...prev,
              attachments: (prev.attachments || []).map((a, i) =>
                i === patch.index ? { ...a, validated: patch.validated } : a
              ),
              all_validated: patch.all_validated,
              validated_count: patch.validated_count,
            }));
          }}
        />
      )}
      {reclassifyTarget && (
        <ReclassifyModal
          dossier={dossier}
          target={reclassifyTarget}
          onClose={() => setReclassifyTarget(null)}
          onSaved={(updated) => {
            setReclassifyTarget(null);
            setDossier((prev) => ({
              ...prev,
              attachments: (prev.attachments || []).map((a, i) =>
                i === updated.idx ? { ...a, ...updated.attachment } : a
              ),
              pieces_presentes: updated.pieces_presentes,
              pieces_manquantes: updated.pieces_manquantes,
            }));
            toast.success("Pièce reclassée");
          }}
        />
      )}
    </AppLayout>
  );
}

const PIECE_TYPES = [
  "Bon de commande",
  "Fiche de renseignements",
  "Fiche contact financement",
  "CNI recto-verso",
  "Avis d'impôt",
  "Taxe foncière",
  "Facture électricité",
  "Facture gaz",
  "Facture eau",
  "Justificatif de domicile",
  "RIB",
  "Fiche de paie",
  "Mandat Mairie",
  "Mandat Enedis",
  "Mandat Mairie et Enedis",
  "Mandat de représentation",
  "Photo compteur Linky",
  "Photo compteur électrique",
  "Photo compteur gaz",
  "Photo maison",
  "Photo chantier",
  "Devis",
  "Autre",
];

function ReclassifyModal({ dossier, target, onClose, onSaved }) {
  const { idx, att } = target;
  const [learnedTypes, setLearnedTypes] = useState([]);
  // Merge built-in + learned categories, with "Autre" always last
  const allTypes = React.useMemo(() => {
    const base = PIECE_TYPES.filter((t) => t !== "Autre");
    const merged = Array.from(new Set([...base, ...learnedTypes])).sort((a, b) => a.localeCompare(b, "fr"));
    return [...merged, "Autre"];
  }, [learnedTypes]);
  const [type, setType] = useState(allTypes.includes(att.type_piece) ? att.type_piece : "Autre");
  const [customType, setCustomType] = useState(!PIECE_TYPES.includes(att.type_piece) ? (att.type_piece || "") : "");
  const [personne, setPersonne] = useState(att.personne || "");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/dossiers/_meta/known-piece-types");
        setLearnedTypes(data.learned || []);
        // Re-resolve "type" once learned categories have arrived
        if (data.learned?.includes(att.type_piece)) {
          setType(att.type_piece);
          setCustomType("");
        }
      } catch { /* ignore */ }
    })();
  }, [att.type_piece]);

  const submit = async () => {
    const finalType = type === "Autre" && customType.trim() ? customType.trim() : type;
    if (!finalType) {
      toast.error("Le type de pièce est obligatoire");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.patch(
        `/dossiers/${dossier.id}/attachment-by-index/${idx}/reclassify`,
        { type_piece: finalType, personne: personne.trim() || null }
      );
      onSaved({
        idx,
        attachment: data.attachment,
        pieces_presentes: data.pieces_presentes,
        pieces_manquantes: data.pieces_manquantes,
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-[60] flex items-center justify-center p-6" onClick={busy ? undefined : onClose}>
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-md border border-norea-border"
        onClick={(e) => e.stopPropagation()}
        data-testid="reclassify-modal"
      >
        <div className="px-6 py-4 border-b border-norea-border flex items-start justify-between">
          <h3 className="font-heading text-xl font-medium text-norea-text flex items-center gap-2">
            <Pencil className="w-5 h-5 text-norea-primary" />
            Reclasser la pièce
          </h3>
          <button onClick={onClose} disabled={busy} className="text-norea-textMuted hover:text-norea-text">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="px-6 py-5 space-y-4">
          <div className="text-xs text-norea-textMuted">
            <div className="overline mb-1">Fichier d'origine</div>
            <div className="font-mono text-norea-text break-all" data-testid="reclassify-origin-name">{att.filename}</div>
            {att.type_piece && (
              <div className="mt-1">Actuellement classé en : <strong>{att.type_piece}</strong></div>
            )}
          </div>

          <div>
            <div className="overline mb-1">Type de pièce</div>
            <select
              value={type}
              onChange={(e) => setType(e.target.value)}
              className="input-field text-sm w-full"
              data-testid="reclassify-type"
            >
              {allTypes.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>

          {type === "Autre" && (
            <div>
              <div className="overline mb-1">Préciser (optionnel)</div>
              <input
                type="text"
                value={customType}
                onChange={(e) => setCustomType(e.target.value)}
                placeholder="ex: Photo façade, Devis client…"
                className="input-field text-sm w-full"
                maxLength={60}
                data-testid="reclassify-custom-type"
              />
            </div>
          )}

          <div>
            <div className="overline mb-1">Personne concernée (optionnel)</div>
            <input
              type="text"
              value={personne}
              onChange={(e) => setPersonne(e.target.value)}
              placeholder="ex: Pierre-Jean GABORY"
              className="input-field text-sm w-full"
              data-testid="reclassify-personne"
            />
            <div className="text-[10px] text-norea-textMuted italic mt-1">
              Utile pour distinguer 2 pièces du même type (ex: 2 CNI emprunteurs).
            </div>
          </div>
        </div>
        <div className="px-6 py-4 bg-norea-bg border-t border-norea-border flex items-center justify-end gap-2">
          <button onClick={onClose} disabled={busy} className="btn-secondary" data-testid="btn-cancel-reclassify">
            Annuler
          </button>
          <button onClick={submit} disabled={busy} className="btn-primary" data-testid="btn-confirm-reclassify">
            {busy ? <RefreshCcw className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            Enregistrer
          </button>
        </div>
      </div>
    </div>
  );
}
